import React, { useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { Checkbox, FormControlLabel, MenuItem, Select } from '@mui/material';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';
import { getAPI, postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

const typographyLabel = {
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 500,
  width: '120px',
};

const sizeSkeletoField = {
  width: '100%',
  height: '48px',
};

const StackCenter = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

interface DialogDetailProps {
  open: boolean;
  doctorid: number;
  date: string;
  reservation_id: string;
  onClose: (isRefreshList?: boolean) => void;
  handleSubmit: () => void;
  fields: any;
}

const DialogDetail: React.FC<DialogDetailProps> = (props) => {
  const notifyContext = useNotify();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<any>({});
  const [fieldSelected, setFieldSelected] = useState('');
  const [fieldValues, setFieldValues] = useState<any>([]);

  useEffect(() => {}, []);

  const handleClose = () => {
    setFieldSelected('')
    setFieldValues([]);
    props.onClose();
  };

  const getDetail = () => {
    const token = getToken();

    if (!token) {
      throw new Error('Failed to get access token');
    }

    getAPI(
      `/op-preparation/status/detail?reservation_id=${props.reservation_id}&token=${token}`
    )
      .then((res) => {
        if (res.code === 200) {
          // setFields(res.data);
          setDetail(res.data || {});
          setFieldValues(res.data?.values || []);
          setFieldSelected(res.data?.category || '');
        }
      })
      .finally(() => {
        // setCheckAPI(true);
      });
  };

  const handleInputChange = (event: any, key: string) => {
    const value = event.target.value;
    let newValues = [...fieldValues];
    newValues.map((val) => {
      if (val.key !== key) return val;
      val.value = value;
      return val;
    });
    setFieldValues(newValues);
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    const checked = event.target.checked;
    let newValues = [...fieldValues];
    newValues.map((val) => {
      if (val.key !== key) return val;
      val.value = checked ? '1' : '0';
      return val;
    });
    setFieldValues(newValues);
  };

  useEffect(() => {
    if (props.open) {
      setIsLoading(false);
      getDetail();
    }

    // eslint-disable-next-line
  }, [props.open]);

  const handleSubmit = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      const data = {
        ...detail,
        category: fieldSelected,
        values: fieldValues,
        reservation_id: props.reservation_id,
        user_id: '1',
      };

      postAPI(`/op-preparation/status?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            props.handleSubmit()
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleSelectChange = (event: any) => {
    const { value } = event.target;
    setFieldSelected(value);
    if (value === '') {
      setFieldValues([]);
      return;
    }
    let newValues = [...props.fields[value]];
    newValues.map((val) => {
      if (val.type === 'bool') {
        val.value = '0';
      } else {
        val.value = '';
      }
      return val;
    });
    setFieldValues(newValues);
    // props.handleChangeValue(name, value);
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '600px',
          m: '0px',
        },
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography
            sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}
          >
            手術準備ステータス
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='Close'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden',
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 210px)', borderWidth: '18px' }}
        >
          <Stack spacing='10px' sx={{ pr: '14px' }}>
            <Stack spacing={'15px'}>
              <Stack>
                {isLoading ? (
                  <>
                    <Skeleton width={'100px'} height={'20px'} />
                    <Skeleton width={'100px'} height={'20px'} />
                  </>
                ) : (
                  <>
                    <Typography fontSize={'12px'} fontWeight={500}>
                      {'たなか　さくらこ'}
                    </Typography>
                    <Typography fontSize={'20px'} fontWeight={500}>
                      {'田中　櫻子'}
                    </Typography>
                  </>
                )}
              </Stack>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '14.4px',
                  fontWeight: '700',
                }}
              >
                小タイトル
              </Typography>
              <Select
                value={fieldSelected}
                displayEmpty
                onChange={(e) => handleSelectChange(e)}
                sx={{
                  backgroundColor: 'primary.light',
                  width: '200px',
                  '& .MuiInputBase-input.MuiOutlinedInput-input': {
                    p: '6px 14px',
                  },
                }}
              >
                <MenuItem value={''} selected={fieldSelected === ''}>
                  選択してください
                </MenuItem>
                {Object.keys(props.fields).map((item: string, idx: number) => {
                  return (
                    <MenuItem value={item} key={idx}>
                      {item}
                    </MenuItem>
                  );
                })}
              </Select>

              {/* <Box sx={{ mt: '10px' }}>
                <Chip
                  label={detail?.category}
                  variant='outlined'
                  // onDelete={() => { }}
                  // deleteIcon={<IconAddCustom />}
                  sx={{
                    mr: '8px',
                    mb: '8px',
                    borderRadius: '4px',
                    p: '4px 8px',
                    borderColor: '#23B7E5',
                    color: '#23B7E5',
                    // width: '50px',
                    height: '24px',
                  }}
                />
              </Box> */}

              <Box sx={{ mt: '20px' }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '14.4px',
                    fontWeight: '700',
                  }}
                >
                  確認状況
                </Typography>
                <Stack spacing={'10px'} mt={'15px'}>
                  {/* <StackCenter>
                    <Typography sx={{ ...typographyLabel }}>
                      モニター
                    </Typography>

                    {!isLoading ? (
                      <InputCustom
                        value={'あいうえおABC123'}
                        type='text'
                        labelInput='テキストを入力してください'
                        name='title'
                        onChange={() => {}}
                        sx={{ ml: '12px', width: '100%' }}
                      />
                    ) : (
                      <Skeleton
                        variant='rounded'
                        sx={{ ...sizeSkeletoField }}
                      />
                    )}
                  </StackCenter> */}
                  {fieldValues &&
                    fieldValues.map((fv: any, index: number) => {
                      return (
                        <StackCenter key={index}>
                          <Typography sx={{ ...typographyLabel }}>
                            {fv.label}
                          </Typography>

                          {!isLoading ? (
                            <>
                              {fv.type === 'text' && (
                                <OutlinedInput
                                  fullWidth
                                  type={'text'}
                                  placeholder={'入力してください'}
                                  name={fv.key}
                                  value={fv.value || ''}
                                  onChange={(event) =>
                                    handleInputChange(event, fv.key)
                                  }
                                  sx={{
                                    width: '100%',
                                    height: '48px',
                                    // '& .MuiOutlinedInput-notchedOutline': { borderColor: props.error ? '#d32f2f !important' : '#BDBDBD' },
                                    '& .MuiInputBase-input': { p: '12px 16px' },
                                    '& input::placeholder': {
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      color: '#AAB2BD',
                                      opacity: 1,
                                    },
                                    ml: '12px',
                                  }}
                                  inputProps={{
                                    sx: {
                                      fontSize: '12px',
                                      fontWeight: 400,
                                    },
                                  }}
                                />
                              )}
                              {fv.type === 'bool' && (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        color: '#A3AED0',
                                      }}
                                      checked={fv.value === '1' ? true : false}
                                      onChange={(event) =>
                                        handleCheckboxChange(event, fv.key)
                                      }
                                      value={fv.value}
                                    />
                                  }
                                  sx={{
                                    ml: '4px',
                                    fontWeight: '500',
                                    fontSize: '14px',
                                  }}
                                  label={''}
                                />
                              )}
                            </>
                          ) : (
                            <Skeleton
                              variant='rounded'
                              sx={{ ...sizeSkeletoField }}
                            />
                          )}
                        </StackCenter>
                      );
                    })}
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </SimpleBar>
      </DialogContent>

      <DialogActions
        style={{
          padding: '16px',
          justifyContent: 'right',
        }}
      >
        <Stack direction={'row'} spacing={'8px'}>
          <BtnOutlineGradient
            text='キャンセル'
            handleButtonClick={handleClose}
            disabled={isLoading}
          />
          <BtnGradient
            text='登録'
            handleButtonClick={handleSubmit}
            disabled={isLoading || fieldSelected === ''}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DialogDetail;
